import { useState } from "react"
import { useTranslation } from "react-i18next"
import { PencilSimple } from "@phosphor-icons/react"
import { Card, CardHeader, CardHeaderTitle } from "@/components/ui/card"
import { DisabledNewChatsDialog } from "@/modules/admin/capacity/components/disabled-new-chats-dialog"
import { useChatAvailabilityQuery } from "@/modules/chats/services/useChatAvailabilityQuery"
import { Language } from "@/types/__generated__/union-types"

interface DisableNewChatsCardProps {
  isMedical: boolean
}

const DisableNewChatsCard = (props: DisableNewChatsCardProps) => {
  const { t } = useTranslation()
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const status = useChatAvailabilityQuery()

  const chatStatusMap = status.chatAvailability.reduce<Record<Language, boolean>>(
    (map, item) => {
      const isActive = props.isMedical ? item.canCreateMedicalChats : item.canCreateAdministrativeChats
      map[item.language] = isActive
      return map
    },
    {
      ENGLISH: false,
      FRENCH: false,
      GERMAN: false,
      ITALIAN: false,
    },
  )

  const renderChatAvailability = (language: string, isActive: boolean) => (
    <span>
      {language}:{" "}
      {isActive ? t("admin.capacity.disabledNewChat.enabled") : t("admin.capacity.disabledNewChat.disabled")}
    </span>
  )

  return (
    <>
      <Card className="h-38.5 w-92.5">
        <CardHeader>
          <div className="flex items-center gap-4">
            <div>
              <CardHeaderTitle>
                {props.isMedical
                  ? t("admin.capacity.disabledNewChat.medicalChatTitle")
                  : t("admin.capacity.disabledNewChat.administrativeChatTitle")}
              </CardHeaderTitle>
            </div>
          </div>
          <div className="flex justify-end">
            <div className="hover:bg-surface-hovered cursor-pointer p-2 hover:rounded-full hover:p-2">
              <PencilSimple className="text-on-surface-subdued" onClick={() => setIsDialogOpen(true)} />
            </div>
          </div>
        </CardHeader>

        <div className="text-on-surface-subdued grid items-stretch gap-0 p-4 pt-0 text-sm leading-5 font-normal">
          {renderChatAvailability("EN", chatStatusMap["ENGLISH"])}
          {renderChatAvailability("FR", chatStatusMap["FRENCH"])}
          {renderChatAvailability("IT", chatStatusMap["ITALIAN"])}
          {renderChatAvailability("DE", chatStatusMap["GERMAN"])}
        </div>
      </Card>

      {isDialogOpen && (
        <DisabledNewChatsDialog
          isMedical={props.isMedical}
          onClose={() => setIsDialogOpen(false)}
          chatStatus={chatStatusMap}
        />
      )}
    </>
  )
}

export { DisableNewChatsCard }
